import { useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * useModelFieldOptions — resolves a select field's options from the backend
 * model schema (fetched + cached by useFetchedSelectionsRequestsHook into
 * `state.fetchedSelections["modelSchema:<objectType>"]`), with a static
 * constant fallback.
 *
 * CONTRACT: this ALWAYS returns a usable array. Live options are used only when
 * they resolve to a NON-EMPTY list of strings; in every other case (schema not
 * loaded yet, field missing, options empty/undefined, unexpected shape) it
 * returns the provided hardcoded `fallback`. So any dropdown swapped onto this
 * hook keeps a hardcoded backup and never renders empty.
 *
 * The `/model_schemas/object/{objectType}` response shape isn't 100% pinned, so
 * we resolve options defensively from several possible shapes:
 *   - `model_fields[].options` (the documented shape) — string[] or {value/label}[]
 *   - `model_fields[].enum | choices | values` — same, alternate keys
 *   - an enum reference (`enum_name` / `enum` string) looked up in a top-level
 *     `enums` map
 *
 * @param {string} fieldName   - model field name, e.g. "loanStatus".
 * @param {Array}  fallback    - constant options used if the schema lacks them.
 * @param {object} [opts]
 * @param {string} [opts.objectType="underwriting"] - which model schema to read.
 * @returns {Array} the live options (string[]) or the fallback.
 */

// Keys a select field's inline options might live under, in priority order.
const FIELD_OPTION_KEYS = ["options", "enum", "choices", "values", "selectOptions"];

// Coerce an options array (string[] or object[]) down to a clean string[].
const normalizeOptions = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((o) => {
      if (typeof o === "string") return o;
      if (o && typeof o === "object") return o.value ?? o.label ?? o.name ?? null;
      return null;
    })
    .filter((v) => typeof v === "string" && v.length > 0);
};

export const useModelFieldOptions = (
  fieldName,
  fallback = [],
  { objectType = "underwriting" } = {},
) => {
  const schema = useSelector(
    (state) => state.fetchedSelections?.[`modelSchema:${objectType}`],
  );

  return useMemo(() => {
    const safeFallback = Array.isArray(fallback) ? fallback : [];
    const modelFields = schema?.model_fields;
    if (!Array.isArray(modelFields)) return safeFallback;

    const field = modelFields.find((f) => f?.name === fieldName);
    if (!field) return safeFallback;

    // 1) Inline options under any of the common keys.
    for (const key of FIELD_OPTION_KEYS) {
      const opts = normalizeOptions(field[key]);
      if (opts.length) return opts;
    }

    // 2) An enum reference resolved against a top-level `enums` map.
    const enumRef = field.enum_name || field.enum || field.enumRef;
    const enums = schema?.enums;
    if (typeof enumRef === "string" && enums && typeof enums === "object") {
      const fromEnum = normalizeOptions(enums[enumRef]?.values ?? enums[enumRef]);
      if (fromEnum.length) return fromEnum;
    }

    // 3) Nothing usable → hardcoded backup.
    return safeFallback;
  }, [schema, fieldName, fallback]);
};

export default useModelFieldOptions;
